/**
 * Recover from stale asset references after a deploy.
 *
 * Lazily-imported chunks (and their CSS) are hashed, so once a new build ships the
 * old files are purged from the CDN. A page opened before the deploy then hits a
 * 404 the moment it lazy-loads a chunk (e.g. the video player or a `t()` catalog), and
 * Vite dispatches `vite:preloadError`. Reload to pull the current assets, budgeted per
 * session so a genuinely broken build cannot loop. The error is left to rethrow so local
 * handlers keep receiving the real rejection.
 *
 * @see https://vite.dev/guide/build.html#load-error-handling
 */
const RELOAD_COUNT_KEY = 'ixdf:preloadErrorReloadCount';
// app.js reads this key back to report the recovery; kept a literal there so the two entries share no chunk.
const PENDING_REPORT_KEY = 'ixdf:preloadErrorPendingReport';
const MAX_RELOADS_PER_SESSION = 2;

// On import rather than from app.js: a module that imports `t()` evaluates only after the
// catalog chunk settles, so a handler registered there would miss that chunk's own failure.
window.addEventListener('vite:preloadError', (event) => {
    try {
        const reloadCount = Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0;
        if (reloadCount >= MAX_RELOADS_PER_SESSION) {
            // Reloading didn't fix it: the build is broken, not merely stale.
            window.dispatchEvent(new CustomEvent('log:error', {
                detail: event.payload ?? new Error('vite:preloadError (reload budget exhausted)'),
            }));
            return;
        }

        sessionStorage.setItem(RELOAD_COUNT_KEY, String(reloadCount + 1));
        sessionStorage.setItem(PENDING_REPORT_KEY, event.payload?.message ?? 'unknown asset');
    } catch {
        // Without sessionStorage there is no loop guard: report instead of reloading.
        window.dispatchEvent(new CustomEvent('log:error', {
            detail: event.payload ?? new Error('vite:preloadError (sessionStorage unavailable)'),
        }));
        return;
    }

    window.location.reload();
});
